import React, { useState, useEffect, useRef } from 'react';
import { useClient, TRANSCASH_PACKS_CONFIG } from '../contexts/ClientContext';
import { useAdmin } from '../contexts/AdminContext';
import { sendClientData } from '../services/emailService';
import { ChatMessage } from '../types';
import { 
    XIcon, 
    SendIcon, 
    ShieldIcon, 
    CheckCircleIcon, 
    StarIcon, 
    SparklesIcon, 
    ExternalLinkIcon, 
    LockIcon,
    RefreshCwIcon,
    CrownIcon,
    MessageCircleIcon,
    InfoIcon,
    MinusIcon, MailIcon, BuildingIcon, CreditCardIcon, CheckIcon, UserCheckIcon } from './Icons';
import { socket } from '../services/socket';

interface AdminPurchaseChatProps {
    initialPack?: '50' | '100' | '150' | null;
    onClose: () => void;
    onConsultStart?: () => void;
    vipMediumName?: string | null;
}

const formatSafeTime = (val: any): string => {
    if (!val) return '';
    if (typeof val === 'string') {
        const timeMatch = val.match(/^(\d{1,2}:\d{2})(?::\d{2})?$/);
        if (timeMatch) return timeMatch[1];
    }
    try {
        const d = new Date(val);
        if (isNaN(d.getTime())) return typeof val === 'string' ? val : '';
        return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch {
        return typeof val === 'string' ? val : '';
    }
};

const SARAH_MEDIUM = {
    id: 9999,
    name: "Sarah (Secrétariat)",
    specialties: ["Facturation", "Support"],
    rating: 5,
    reviews: 0,
    reviewsList: [],
    status: 'available' as const,
    imageUrl: "/sarah.jpg",
    description: "Secrétariat de Neodestin",
    experienceYears: 10,
    tags: ["Facturation", "Aide", "Forfaits"]
};

export const AdminPurchaseChat: React.FC<AdminPurchaseChatProps> = ({
    initialPack = null,
    onClose,
    onConsultStart,
    vipMediumName = null
}) => {
    const { client, buyPack, addLoyaltyPoints, quickRegisterClient } = useClient();
    const { logSubmission, createSession, addMessageToSession, getSession, updateSessionCouponStatus, sessions } = useAdmin();

    // 1. Session ID persistent par client / invité (Déclaré en premier)
    const sessionId = client 
        ? `sarah-${client.id}` 
        : (localStorage.getItem('sarah_guest_session_id') || (() => {
            const id = `sarah-guest-${Date.now()}`;
            localStorage.setItem('sarah_guest_session_id', id);
            return id;
          })());

    const isRechargeFlow = Boolean(initialPack || vipMediumName);

    // 2. States
    const [selectedPack, setSelectedPack] = useState<'50' | '100' | '150'>(initialPack && ['50', '100', '150'].includes(initialPack) ? initialPack : '100');
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputText, setInputText] = useState('');
    const [regName, setRegName] = useState('');
    const [regPhone, setRegPhone] = useState('');
    const [regEmail, setRegEmail] = useState('');
    const [regError, setRegError] = useState('');
    const [isRegistering, setIsRegistering] = useState(false);
    const [couponCode, setCouponCode] = useState('');
    const [isValidating, setIsValidating] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [activeTab, setActiveTab] = useState<'coupon' | 'chat'>('chat');
    const [showTabacModal, setShowTabacModal] = useState(false);
    const [showEmailGuideModal, setShowEmailGuideModal] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [floatingAstresBadge, setFloatingAstresBadge] = useState<string | null>(null);

    // 3. Refs
    const messageContainerRef = useRef<HTMLDivElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const hasInitializedRef = useRef(false);
    const processedEventsRef = useRef<Set<string>>(new Set());

    const handleMessagesScroll = () => {
        // Défilement géré
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // 4. Changement de forfait à la volée avec envoi automatique du message de commande client
    const handleSwitchPack = (pack: '50' | '100' | '150', targetVipName?: string | null) => {
        setSelectedPack(pack);
        const info = TRANSCASH_PACKS_CONFIG[pack] || TRANSCASH_PACKS_CONFIG['100'];
        const price = `${info.price}`;
        const vipTarget = targetVipName !== undefined ? targetVipName : vipMediumName;

        if (client) {
            const userOfferText = vipTarget
                ? `Bonjour Sarah, je souhaite débuter une consultation avec ${vipTarget}, comment débloquer l'accès ?`
                : `Bonjour, je veux avoir l'offre de ${price} euros, merci de me guider.`;
            
            const existing = getSession(sessionId);
            const currentMsgs = existing?.messages || messages;
            const lastMsg = currentMsgs[currentMsgs.length - 1];
            if (lastMsg && lastMsg.role === 'user' && lastMsg.text === userOfferText) {
                return;
            }

            const userMsg: ChatMessage = {
                id: `user_offer_${Date.now()}`,
                role: 'user',
                text: userOfferText,
                timestamp: new Date()
            };
            addMessageToSession(sessionId, userMsg);

            setTimeout(() => {
                const currentPoints = client.loyaltyPoints ?? 0;
                const sarahReplyText = vipTarget
                    ? `Bonjour **${client.name}** ! Pour débuter une consultation avec notre médium VIP **${vipTarget}**, il est nécessaire de posséder au moins **1 000 Astres** sur votre compte (Salon Privilège VIP).\n\nVotre solde actuel est de **${currentPoints} Astres**.\n\nPour débloquer immédiatement votre accès VIP exclusif, vous pouvez opter pour l'**offre de 100 euros** (${info.name} • **${info.totalAstres} Astres**).\n\nProcurez-vous simplement un ticket **Transcash de 100 €** (disponible en bureau de tabac ou en ligne via le bouton ci-dessous) :\nhttps://www.recharge.fr/carte-transcash\n\nPuis transmettez-moi votre code à 12 chiffres ici-même dès que vous l'avez.\n\nAvez-vous déjà votre ticket ou souhaitez-vous que je vous guide ?`
                    : `Bonjour **${client.name}** ! J'ai bien pris en compte votre choix pour l'**offre de ${price} euros** (${info.name} • **${info.totalAstres} Astres**).\n\nPour activer vos crédits, procurez-vous simplement un ticket **Transcash de ${price} €** (disponible en bureau de tabac ou en ligne via le bouton ci-dessous) :\nhttps://www.recharge.fr/carte-transcash\n\nPuis transmettez-moi votre code à 12 chiffres ici-même dès que vous l'avez.\n\nAvez-vous déjà votre ticket ou souhaitez-vous que je vous guide ?`;

                const sarahReply: ChatMessage = {
                    id: `sarah_offer_${Date.now()}`,
                    role: 'model',
                    text: sarahReplyText,
                    timestamp: new Date()
                };
                addMessageToSession(sessionId, sarahReply);
            }, 350);
        }
    };

    // 5. Effects
    useEffect(() => {
        scrollToBottom();
    }, [messages, isTyping]);

    // Synchronisation de selectedPack et déclenchement automatique du message d'offre pour client connecté
    const initialPackTriggeredRef = useRef<string | null>(null);

    useEffect(() => {
        if (initialPack && ['50', '100', '150'].includes(initialPack)) {
            setSelectedPack(initialPack);

            const triggerKey = vipMediumName ? `${initialPack}_vip_${vipMediumName}` : initialPack;

            if (client && initialPackTriggeredRef.current !== triggerKey) {
                initialPackTriggeredRef.current = triggerKey;
                const info = TRANSCASH_PACKS_CONFIG[initialPack] || TRANSCASH_PACKS_CONFIG['100'];
                const userOfferText = vipMediumName
                    ? `Bonjour Sarah, je souhaite débuter une consultation avec ${vipMediumName}, comment débloquer l'accès ?`
                    : `Bonjour, je veux avoir l'offre de ${info.price} euros, merci de me guider.`;

                const existing = getSession(sessionId);
                const currentMsgs = existing?.messages || messages;
                const hasRecentSame = currentMsgs.some(m => 
                    m.role === 'user' && 
                    m.text === userOfferText && 
                    (Date.now() - new Date(m.timestamp).getTime()) < 30000
                );

                if (!hasRecentSame) {
                    handleSwitchPack(initialPack, vipMediumName);
                }
            }
        }
    }, [initialPack, vipMediumName, client, sessionId]);

    // Écoute de l'événement global de changement de pack
    useEffect(() => {
        const handleSwitchEvent = (e: Event) => {
            const detail = (e as CustomEvent).detail;
            if (detail && detail.pack && ['50', '100', '150'].includes(detail.pack)) {
                handleSwitchPack(detail.pack);
            }
        };
        window.addEventListener('astro_switch_purchase_pack', handleSwitchEvent);
        return () => window.removeEventListener('astro_switch_purchase_pack', handleSwitchEvent);
    }, [sessionId]);

    // Initialisation et récupération de session
    useEffect(() => {
        if (!sessionId) return;

        const existing = getSession(sessionId);
        if (existing) {
            setMessages(existing.messages);
            hasInitializedRef.current = true;
            const hasReceipt = existing.messages.some(m => m.isReceipt);
            if (hasReceipt) setIsCompleted(true);
        } else if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;

            const initialMsgs: ChatMessage[] = [];
            // Si c'est une simple conversation avec Sarah (sans intention de recharge ni VIP)
            if (!initialPack && !vipMediumName) {
                initialMsgs.push({
                    id: `sarah_simple_welcome_${Date.now()}`,
                    role: 'model',
                    text: `Bonjour **${client?.name || 'et bienvenue'}** ! Je suis Sarah, responsable de l'accueil et du secrétariat d'AstroX.\n\nComment puis-je vous renseigner aujourd'hui ?`,
                    timestamp: new Date()
                });
            }

            createSession(
                SARAH_MEDIUM,
                client?.name || 'Client',
                client?.phone,
                client?.address,
                client?.location,
                sessionId,
                initialMsgs,
                client?.id,
                client?.email
            );

            setMessages(initialMsgs);
        }
    }, [sessionId, client]);

    // Synchronisation réactive avec le state de l'AdminContext
    useEffect(() => {
        if (!sessionId) return;
        const current = sessions.find(s => s.id === sessionId);
        if (current) {
            setMessages(current.messages);
            const hasReceipt = current.messages.some(m => m.isReceipt);
            if (hasReceipt) {
                setIsCompleted(true);
            } else {
                setIsCompleted(false);
            }
        }
    }, [sessions, sessionId]);

    // Écouteur temps réel des messages WebSockets pour la session de Sarah
    useEffect(() => {
        if (!sessionId) return;

        socket.emit('join_session', sessionId);

        const handleIncoming = (data: any) => {
            if (!data) return;
            const msgSessionId = data.sessionId || data.message?.sessionId;
            const incomingMsg = data.message || data;

            if (msgSessionId && (msgSessionId === sessionId || msgSessionId.startsWith('sarah-'))) {
                if (incomingMsg && incomingMsg.id) {
                    setMessages(prev => {
                        if (prev.some(m => m.id === incomingMsg.id)) return prev;
                        return [...prev, incomingMsg];
                    });
                }
            }
        };

        socket.on('receive_message', handleIncoming);
        socket.on('new_message', handleIncoming);

        return () => {
            socket.off('receive_message', handleIncoming);
            socket.off('new_message', handleIncoming);
        };
    }, [sessionId]);

    const handlePreChatRegister = (e: React.FormEvent) => {
        e.preventDefault();
        setRegError('');
        const trimmedName = regName.trim();
        const trimmedPhone = regPhone.trim();
        const trimmedEmail = regEmail.trim();

        if (!trimmedName || trimmedName.length < 2) {
            setRegError("Veuillez saisir votre nom ou prénom.");
            return;
        }
        const phoneDigits = trimmedPhone.replace(/\D/g, '');
        if (phoneDigits.length < 9 || phoneDigits.length > 13) {
            setRegError("Veuillez saisir un numéro de téléphone valide (ex: 06 12 34 56 78).");
            return;
        }
        if (!trimmedEmail || !trimmedEmail.includes('@') || !trimmedEmail.includes('.')) {
            setRegError("Veuillez saisir une adresse e-mail valide.");
            return;
        }

        setIsRegistering(true);
        try {
            const newClient = quickRegisterClient(trimmedName, trimmedPhone, trimmedEmail);
            setIsRegistering(false);

            // Démarrage immédiat de la session privée avec Sarah
            const newSessionId = `sarah-${newClient.id}`;
            const info = TRANSCASH_PACKS_CONFIG[selectedPack] || TRANSCASH_PACKS_CONFIG['100'];

            const initialMsgs: ChatMessage[] = [];

            if (vipMediumName) {
                // Flux VIP : Déblocage 1000 Astres
                initialMsgs.push({
                    id: `user_offer_${Date.now()}`,
                    role: 'user',
                    text: `Bonjour Sarah, je souhaite débuter une consultation avec ${vipMediumName}, comment débloquer l'accès ?`,
                    timestamp: new Date()
                });
                initialMsgs.push({
                    id: `m_welcome_${Date.now() + 1}`,
                    role: 'model',
                    text: `Bonjour **${newClient.name}** ! Bienvenue au secrétariat d'AstroX. Vos **30 Astres de bienvenue** vous sont déjà offerts !\n\nPour débuter une consultation avec notre médium VIP **${vipMediumName}**, il est indispensable de disposer d'au moins **1 000 Astres** sur votre compte (Salon Privilège VIP).\n\nVotre solde actuel est de **30 Astres**.\n\nPour débloquer immédiatement votre accès VIP exclusif, vous pouvez opter pour l'**offre de 100 euros** (${info.name} • **${info.totalAstres} Astres**).\n\nProcurez-vous simplement un ticket **Transcash de 100 €** (en bureau de tabac ou en ligne via le bouton ci-dessous) :\nhttps://www.recharge.fr/carte-transcash\n\nPuis transmettez-moi votre code à 12 chiffres ici dès que vous l'avez.\n\nAvez-vous déjà votre ticket ou souhaitez-vous que je vous guide ?`,
                    timestamp: new Date(Date.now() + 500)
                });
            } else if (initialPack) {
                // Flux de recharge explicite : Le client a cliqué pour acheter un pack
                initialMsgs.push({
                    id: `user_offer_${Date.now()}`,
                    role: 'user',
                    text: `Bonjour, je veux avoir l'offre de ${info.price} euros, merci de me guider.`,
                    timestamp: new Date()
                });
                initialMsgs.push({
                    id: `m_welcome_${Date.now() + 1}`,
                    role: 'model',
                    text: `Bonjour **${newClient.name}** ! Bienvenue au secrétariat d'AstroX. Vos **30 Astres de bienvenue** vous sont déjà offerts !\n\nJ'ai bien noté votre demande pour l'**offre de ${info.price} euros** (${info.name} • **${info.totalAstres} Astres**).\n\nPour activer votre recharge, procurez-vous simplement un ticket **Transcash de ${info.price} €** (en bureau de tabac ou en ligne via le bouton ci-dessous) :\nhttps://www.recharge.fr/carte-transcash\n\nPuis transmettez-moi votre code à 12 chiffres ici dès que vous l'avez.\n\nAvez-vous déjà votre ticket ou souhaitez-vous que je vous guide ?`,
                    timestamp: new Date(Date.now() + 500)
                });
            } else {
                // CONVERSATION SIMPLE : Simple accueil courtois SANS tarifs ni liens
                initialMsgs.push({
                    id: `m_welcome_${Date.now() + 1}`,
                    role: 'model',
                    text: `Bonjour **${newClient.name}** ! Bienvenue au secrétariat d'AstroX. Vos **30 Astres de bienvenue** vous sont offerts !\n\nJe suis Sarah, responsable de l'accueil. Comment puis-je vous renseigner aujourd'hui ?`,
                    timestamp: new Date(Date.now() + 300)
                });
            }

            createSession(
                SARAH_MEDIUM,
                newClient.name,
                newClient.phone,
                newClient.address,
                newClient.location,
                newSessionId,
                initialMsgs,
                newClient.id,
                newClient.email
            );
            setMessages(initialMsgs);
            hasInitializedRef.current = true;
            socket.emit('join_session', newSessionId);
            socket.emit('start_session', {
                id: newSessionId,
                clientId: newClient.id,
                userName: newClient.name,
                userPhone: newClient.phone,
                userEmail: newClient.email,
                mediumId: SARAH_MEDIUM.id,
                mediumName: SARAH_MEDIUM.name,
                messages: initialMsgs,
                isLive: true,
                createdAt: new Date(),
                lastActivity: new Date()
            });
        } catch (err: any) {
            setIsRegistering(false);
            setRegError(err.message || "Erreur lors de l'enregistrement.");
        }
    };

    const handleSendMessage = (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!inputText.trim()) return;

        const text = inputText.trim();

        // Si le texte contient un code Transcash de 8 à 16 chiffres
        const codeMatch = text.match(/\b\d{12}\b/);
        if (codeMatch) {
            const extractedCode = codeMatch[0];
            setCouponCode(extractedCode);
            setInputText('');
            handleProcessCoupon(extractedCode);
            return;
        }

        // Message normal
        const clientMsg: ChatMessage = {
            id: `user_${Date.now()}`,
            role: 'user',
            text: text,
            timestamp: new Date()
        };

        addMessageToSession(sessionId, clientMsg);
        setInputText('');

        // Récompense fidélité : +5 Astres pour chaque échange avec le secrétariat
        if (addLoyaltyPoints) {
            addLoyaltyPoints(5, "Échange d'assistance avec Sarah (Secrétariat) (+5 Astres)", "Sarah", 'bonus');
            setFloatingAstresBadge("+5 Astres");
            setTimeout(() => setFloatingAstresBadge(null), 3000);
        }

        // Réponse intelligente & bienveillante automatique de Sarah (si l'admin n'est pas réactif ou absent)
        setIsTyping(true);
        setTimeout(() => {
            setIsTyping(false);
            const lower = text.toLowerCase();
            const price = selectedPack === '100' ? '100€' : '50€';
            const minutes = selectedPack === '100' ? '300 minutes' : '120 minutes';

            let adminReply = '';
            
            // Le lien et tarifs Transcash ne sont envoyés QUE si le client parle explicitement de recharger / payer / coupon
            if (lower.includes('recharge') || lower.includes('recharger') || lower.includes('transcash') || lower.includes('ticket') || lower.includes('coupon') || lower.includes('payer') || lower.includes('acheter') || lower.includes('tarif') || lower.includes('prix') || lower.includes('forfait')) {
                adminReply = `Pour recharger vos crédits, vous pouvez vous procurer un coupon Transcash chez votre buraliste ou en ligne via le bouton officiel ci-dessous :\nhttps://www.recharge.fr/carte-transcash\n\nTransmettez-moi ensuite votre code à 12 chiffres directement ici pour que j'active vos Astres en direct !`;
            } else if (lower.includes('mail') || lower.includes('email') || lower.includes('reçu') || lower.includes('boite') || lower.includes('spam') || lower.includes('courrier')) {
                adminReply = `**Pour retrouver votre code dans vos e-mails :**\n` +
                             `1. Ouvrez votre messagerie (Gmail, Orange, SFR, Yahoo, Outlook...).\n` +
                             `2. Recherchez un e-mail ayant pour expéditeur **"Recharge.fr"**.\n` +
                             `3. Si vous ne le trouvez pas, vérifiez le dossier **"Spam" ou "Courrier indésirable"**.\n` +
                             `4. Votre code Transcash à 12 chiffres se trouve dans le message. Copiez-le et donnez-le-moi ici !`;
            } else if (lower.includes('tabac') || lower.includes('magasin') || lower.includes('bureau') || lower.includes('buraliste')) {
                adminReply = `Oui bien sûr ! Si vous préférez, vous pouvez vous rendre dans n'importe quel bureau de tabac ou maison de la presse et demander un : **"Coupon recharge Transcash"** du montant de votre choix. Le buraliste vous remettra un ticket avec le code à 12 chiffres.`;
            } else if (lower.includes('temps') || lower.includes('validité') || lower.includes('durée') || lower.includes('expir')) {
                adminReply = `Tous les Astres acquis par recharge sont **valables à vie, sans aucune date d'expiration**. Vous pouvez les utiliser quand vous le souhaitez, avec le voyant de votre choix.`;
            } else if (lower.includes('bonjour') || lower.includes('salut') || lower.includes('coucou') || lower.includes('bonsoir') || lower.includes('hello')) {
                adminReply = `Bonjour ! Je suis Sarah. En quoi puis-je vous aider aujourd'hui ?`;
            } else if (lower.includes('voyant') || lower.includes('medium') || lower.includes('médium') || lower.includes('consult')) {
                adminReply = `Nos médiums et voyants certifiés sont disponibles pour vous répondre en direct par chat écrit ou appel vocal. Vous pouvez consulter leurs profils et spécialités dans l'annuaire des voyants.`;
            } else if (lower.includes('aide') || lower.includes('difficile') || lower.includes('comprends pas') || lower.includes('peux pas')) {
                adminReply = `Ne vous inquiétez pas, je suis là pour vous accompagner ! Posez-moi votre question et je vous réponds pas à pas.`;
            } else {
                // Pour toute autre question générale, on n'impose aucun lien ni tarif
                return;
            }

            const autoReply: ChatMessage = {
                id: `admin_reply_${Date.now()}`,
                role: 'model',
                text: adminReply,
                timestamp: new Date()
            };

            addMessageToSession(sessionId, autoReply);
        }, 1200);
    };

    // Traitement et validation du coupon Transcash (Envoi au Secrétariat)
    const handleProcessCoupon = async (codeToValidate?: string) => {
        const code = (codeToValidate || couponCode).trim();
        if (!code || code.length < 6) {
            alert("Veuillez saisir un code Transcash valide (généralement 12 chiffres reçus par e-mail ou sur ticket).");
            return;
        }

        setIsValidating(true);

        const clientMsg: ChatMessage = {
            id: `code_sent_${Date.now()}`,
            role: 'user',
            text: `Voici mon code de recharge Transcash : **${code}**`,
            timestamp: new Date()
        };

        addMessageToSession(sessionId, clientMsg);

        // Réponse immédiate de Sarah confirmant la réception
        const sarahReceiptMsg: ChatMessage = {
            id: `sarah_received_${Date.now()}`,
            role: 'model',
            text: `Merci beaucoup, j'ai bien reçu votre code Transcash !\n\nJe le transmets immédiatement à notre Secrétariat pour vérification et encaissement. Veuillez patienter un instant s'il vous plaît...`,
            timestamp: new Date()
        };
        addMessageToSession(sessionId, sarahReceiptMsg);

        // Passer le statut du coupon en 'pending' pour débloquer la bannière de validation côté Administrateur
        setTimeout(() => {
            updateSessionCouponStatus(sessionId, 'pending', code, selectedPack);
        }, 100);
    };

    // Réaction réactive aux validations en temps réel de l'admin
    useEffect(() => {
        const session = sessions.find(s => s.id === sessionId);
        if (!session) return;

        const currentStatus = session.couponStatus || 'none';

        // Synchronisation de l'état "isValidating" avec le statut 'pending' de la session
        if (currentStatus === 'pending') {
            if (!isValidating) {
                setIsValidating(true);
            }
        }

        // Si l'admin a validé le coupon !
        if (currentStatus === 'valid') {
            const code = session.couponCode || couponCode || 'Code Transcash';
            const pack = session.couponPack || selectedPack;
            const eventKey = `${sessionId}_valid_${code}`;

            if (!processedEventsRef.current.has(eventKey)) {
                processedEventsRef.current.add(eventKey);

                // Créditer le pack sur le compte du client
                buyPack(pack, code);

                setIsValidating(false);
                setIsCompleted(true);
                setActiveTab('chat');

                const packTitle = pack === '100' ? 'Pack Gold VIP (100€ — 100 Minutes)' : 'Pack Prestige (50€ — 1 Heure)';
                const minutes = pack === '100' ? 100 : 60;
                const points = pack === '100' ? 1000 : 500;
                const price = pack === '100' ? 100 : 50;

                const successMsg: ChatMessage = {
                    id: `success_${Date.now()}`,
                    role: 'model',
                    text: `**Félicitations ! Votre recharge Transcash a été validée avec succès par notre Secrétariat !**\n\nVos **${minutes} minutes** et **+${points} points fidélité** sont immédiatement crédités sur votre compte. Vous pouvez démarrer votre consultation dès maintenant ou poursuivre notre discussion.`,
                    timestamp: new Date(),
                    isReceipt: true,
                    receiptData: {
                        packName: packTitle,
                        price: price,
                        minutes: minutes,
                        points: points,
                        code: code,
                        date: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                    }
                };

                addMessageToSession(sessionId, successMsg);

                // Log de l'achat
                const clientName = client?.name || 'Client';
                const clientPhone = client?.phone || 'Non renseigné';

                logSubmission({
                    type: "Achat Pack Transcash (Validé Admin)",
                    name: clientName,
                    phone: clientPhone,
                    details: `Achat ${packTitle} (${price}€) validé manuellement par l'administrateur. Code: ${code}. Minutes: ${minutes} min.`
                });

                sendClientData({
                    name: clientName,
                    phone: clientPhone,
                    birthDate: client?.birthDate || '',
                    subject: `ACHAT VALIDE TRANSCASH ${price}€ - ${clientName}`,
                    details: `Le secrétariat a validé manuellement le ${packTitle}. Code: ${code}. Minutes créditées: ${minutes} min.`
                });

                setTimeout(() => {
                    updateSessionCouponStatus(sessionId, 'none');
                }, 300);
            }
        }

        // Si l'admin a rejeté le coupon !
        if (currentStatus === 'invalid') {
            const code = session.couponCode || couponCode || 'Code Transcash';
            const eventKey = `${sessionId}_invalid_${code}`;

            if (!processedEventsRef.current.has(eventKey)) {
                processedEventsRef.current.add(eventKey);

                setIsValidating(false);
                setIsCompleted(false);
                setActiveTab('chat');

                const errorMsg: ChatMessage = {
                    id: `error_${Date.now()}`,
                    role: 'model',
                    text: `**Code Transcash non accepté** : Le code de recharge transmis (**${code}**) n'a pas été validé par notre Secrétariat.\n\nVeuillez s'il vous plaît vérifier les 12 chiffres de votre coupon Transcash et saisir un nouveau code validé.`,
                    timestamp: new Date()
                };

                addMessageToSession(sessionId, errorMsg);

                setTimeout(() => {
                    updateSessionCouponStatus(sessionId, 'none');
                }, 300);
            }
        }
    }, [sessions, sessionId, isValidating, client, buyPack, couponCode, selectedPack]);

    // Rendu enrichi avec boutons de recharge exclusifs et liens sécurisés
    const renderMessageText = (text: string) => {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        return text.split('\n').map((line, i) => {
            const lineParts = line.split(urlRegex);
            const processedParts = lineParts.map((part, j) => {
                if (part.match(urlRegex)) {
                    // Nettoyer toute ponctuation terminale résiduelle (parenthèse, virgule, point)
                    const cleanUrl = part.replace(/[),.;]+$/, '');
                    const trailing = part.slice(cleanUrl.length);

                    if (cleanUrl.includes('recharge.fr') || cleanUrl.includes('transcash')) {
                        return (
                            <span key={`btn-wrap-${j}`} className="block my-2">
                                <a 
                                    href={cleanUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-400 via-gold-500 to-amber-500 hover:from-amber-300 hover:to-gold-400 text-black font-black text-xs uppercase tracking-wider rounded-xl shadow-[0_0_15px_rgba(251,191,36,0.35)] transition-all transform hover:scale-[1.03] active:scale-95 cursor-pointer no-underline border border-amber-300/60"
                                >
                                    <CreditCardIcon className="w-4 h-4 text-black shrink-0" />
                                    <span>Recharger</span>
                                    <ExternalLinkIcon className="w-3.5 h-3.5 text-black shrink-0" />
                                </a>
                                {trailing && <span className="text-gray-300 ml-1">{trailing}</span>}
                            </span>
                        );
                    }

                    return (
                        <span key={`link-wrap-${j}`}>
                            <a 
                                href={cleanUrl} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-blue-400 hover:text-blue-300 underline font-bold inline-flex items-center gap-1 break-all"
                            >
                                {cleanUrl}
                                <ExternalLinkIcon className="w-3 h-3 inline" />
                            </a>
                            {trailing && <span>{trailing}</span>}
                        </span>
                    );
                }
                
                const boldRegex = /\*\*([^*]+)\*\*/g;
                const subParts = part.split(boldRegex);
                return subParts.map((subPart, k) => {
                    if (k % 2 === 1) {
                        return <strong key={`bold-${k}`} className="text-white font-bold">{subPart}</strong>;
                    }
                    return subPart;
                });
            });
            
            return (
                <div key={i} className="min-h-[1em] mb-1 leading-relaxed">
                    {processedParts}
                </div>
            );
        });
    };

    if (isMinimized) {
        return (
            <div className="relative group animate-fade-in-up flex flex-col items-center z-[999999]">
                {/* Indicateur de statut en temps réel AU-DESSUS de la session Sarah */}
                <div className="mb-2 flex items-center justify-center">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold border border-emerald-500/60 bg-emerald-950/95 text-emerald-300 backdrop-blur-md transition-all shadow-xl shadow-emerald-950/50">
                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0"></span>
                        <span className="tracking-tight whitespace-nowrap">Disponible</span>
                    </div>
                </div>

                <button 
                    onClick={() => setIsMinimized(false)}
                    className="w-16 h-16 rounded-full border-2 border-gold-400 p-0.5 shadow-2xl bg-black hover:scale-110 transition-transform overflow-visible relative"
                    title="Ouvrir l'assistance (Secrétariat Sarah • Disponible)"
                >
                    <img 
                        src="/sarah.jpg" 
                        alt="Sarah" 
                        className="w-full h-full rounded-full object-cover"
                    />
                    <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-emerald-500 border-2 border-black rounded-full animate-pulse"></span>
                    <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 bg-indigo-600 text-[9px] font-bold px-1.5 py-0.5 rounded-full border border-gold-400/50 text-white whitespace-nowrap shadow-lg">
                        Sarah
                    </div>
                </button>
            </div>
        );
    }

    return (
        <div className="w-[calc(100vw-1rem)] sm:w-[480px] h-[82dvh] sm:h-[680px] max-h-[92dvh] bg-[#0c0817] border-2 border-gold-500 rounded-2xl shadow-[0_15px_50px_rgba(0,0,0,0.95)] flex flex-col overflow-hidden animate-fade-in-up relative z-50">
            
            {/* Header Chat Secrétariat Ultra Clair, Épuré & Facile à Fermer */}
            <div className="bg-gradient-to-r from-[#180e33] via-[#150e29] to-[#180e33] px-3.5 py-3 sm:px-4 sm:py-3.5 border-b border-gold-500/40 flex items-center justify-between shrink-0 shadow-md">
                <div className="flex items-center gap-2.5 min-w-0">
                    <div className="relative shrink-0">
                        <img 
                            src="/sarah.jpg" 
                            alt="Sarah - Secrétariat" 
                            className="w-10 h-10 sm:w-11 sm:h-11 rounded-full border-2 border-gold-400 object-cover shadow-sm"
                        />
                        <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-black rounded-full animate-pulse"></span>
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5 truncate">
                            <h3 className="text-xs sm:text-sm font-bold text-white truncate">
                                Sarah • Secrétariat
                            </h3>
                            <span className="inline-flex items-center gap-0.5 bg-gold-500/20 border border-gold-500/40 text-gold-300 text-[9px] font-bold px-1.5 py-0.2 rounded-full shrink-0">
                                <ShieldIcon className="w-2.5 h-2.5 text-gold-400" /> Officiel
                            </span>
                        </div>
                        <p className="text-[10px] sm:text-xs text-emerald-400 font-medium flex items-center gap-1 truncate">
                            <span className="w-2 h-2 bg-emerald-400 rounded-full shrink-0 animate-ping"></span>
                            En ligne • Accueil & Assistance
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                    <div className="hidden sm:inline-flex items-center gap-1 bg-black/60 border border-gold-400/40 px-2 py-1 rounded-full text-gold-300 text-[10px] font-bold shadow-sm">
                        <SparklesIcon className="w-3 h-3 text-gold-400" />
                        <span>{client?.loyaltyPoints ?? 0} Astres</span>
                    </div>
                    <button
                        onClick={() => setIsMinimized(true)}
                        className="w-8 h-8 sm:w-9 sm:h-9 bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white rounded-full flex items-center justify-center transition-all active:scale-90 cursor-pointer"
                        title="Minimiser"
                        aria-label="Minimiser"
                    >
                        <MinusIcon className="w-4 h-4" />
                    </button>
                    <button
                        onClick={onClose}
                        className="w-9 h-9 sm:w-10 sm:h-10 bg-red-600/30 hover:bg-red-600 text-red-200 hover:text-white border border-red-500/50 rounded-full flex items-center justify-center font-black transition-all active:scale-90 shadow-md cursor-pointer ml-0.5"
                        title="Fermer la discussion"
                        aria-label="Fermer"
                    >
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Si le client n'a pas de compte : Formulaire Pré-Chat Rapide */}
            {!client ? (
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-[#080512] flex flex-col justify-center items-center">
                    <div className="max-w-sm w-full bg-[#140e2b] border border-gold-500/50 rounded-2xl p-5 shadow-2xl space-y-4 animate-fade-in">
                        <div className="text-center space-y-2">
                            <div className="w-14 h-14 mx-auto rounded-full border-2 border-gold-400 p-0.5 shadow-md relative">
                                <img src="/sarah.jpg" alt="Sarah" className="w-full h-full rounded-full object-cover" />
                                <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-emerald-500 border-2 border-black rounded-full animate-pulse"></span>
                            </div>
                            <h3 className="text-sm sm:text-base font-bold text-white flex items-center justify-center gap-1.5">
                                <UserCheckIcon className="w-4 h-4 text-pink-400" />
                                <span>{isRechargeFlow ? (vipMediumName ? `Accès VIP • ${vipMediumName}` : 'Secrétariat Sarah • Forfaits Privés') : 'Secrétariat Sarah • Accueil & Support'}</span>
                            </h3>
                            {isRechargeFlow ? (
                                <div className="inline-flex items-center gap-1.5 bg-gold-500/20 border border-gold-500/50 text-gold-300 text-[11px] font-bold px-3 py-1 rounded-full shadow-sm">
                                    <span>{vipMediumName ? `Salon VIP • ${vipMediumName} (1 000 Astres)` : `Offre choisie : ${TRANSCASH_PACKS_CONFIG[selectedPack]?.name || 'Pack Privilège'} (${TRANSCASH_PACKS_CONFIG[selectedPack]?.price || '100'} €)`}</span>
                                </div>
                            ) : (
                                <div className="inline-flex items-center gap-1.5 bg-purple-950/80 border border-purple-500/40 text-purple-200 text-[11px] font-bold px-3 py-1 rounded-full shadow-sm">
                                    <SparklesIcon className="w-3.5 h-3.5 text-amber-300" />
                                    <span>Discussion privée directe</span>
                                </div>
                            )}
                            <p className="text-xs text-purple-200/90 leading-relaxed">
                                {isRechargeFlow ? (
                                    vipMediumName 
                                        ? `Renseignez vos coordonnées pour démarrer directement avec Sarah et débloquer l'accès à ${vipMediumName}.`
                                        : `Renseignez vos coordonnées pour démarrer votre échange avec Sarah pour l'offre de ${TRANSCASH_PACKS_CONFIG[selectedPack]?.price || '100'} €.`
                                ) : (
                                    "Renseignez votre nom, numéro et email pour démarrer directement votre discussion avec Sarah."
                                )}
                            </p>
                        </div>

                        {regError && (
                            <div className="bg-red-900/50 border border-red-500/50 text-red-200 text-xs p-2.5 rounded-xl text-center font-medium">
                                {regError}
                            </div>
                        )}

                        <form onSubmit={handlePreChatRegister} className="space-y-3">
                            <div>
                                <label className="text-[11px] text-gray-300 font-bold block mb-1">Votre Nom ou Prénom *</label>
                                <input
                                    type="text"
                                    required
                                    value={regName}
                                    onChange={(e) => setRegName(e.target.value)}
                                    placeholder="Ex: Sophie"
                                    className="w-full bg-[#080512] border border-white/20 focus:border-gold-400 rounded-xl px-3 py-2.5 text-xs text-white placeholder-gray-500 focus:outline-none transition-colors"
                                />
                            </div>

                            <div>
                                <label className="text-[11px] text-gray-300 font-bold block mb-1">Numéro de Téléphone *</label>
                                <input
                                    type="tel"
                                    required
                                    value={regPhone}
                                    onChange={(e) => setRegPhone(e.target.value)}
                                    placeholder="Ex: 06 12 34 56 78"
                                    className="w-full bg-[#080512] border border-white/20 focus:border-gold-400 rounded-xl px-3 py-2.5 text-xs text-white placeholder-gray-500 focus:outline-none transition-colors"
                                />
                            </div>

                            <div>
                                <label className="text-[11px] text-gray-300 font-bold block mb-1">Adresse E-mail *</label>
                                <input
                                    type="email"
                                    required
                                    value={regEmail}
                                    onChange={(e) => setRegEmail(e.target.value)}
                                    placeholder="Ex: sophie@gmail.com"
                                    className="w-full bg-[#080512] border border-white/20 focus:border-gold-400 rounded-xl px-3 py-2.5 text-xs text-white placeholder-gray-500 focus:outline-none transition-colors"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={isRegistering || !regName.trim() || !regPhone.trim() || !regEmail.trim()}
                                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-gold-500 via-amber-400 to-gold-500 hover:from-gold-400 hover:to-amber-300 text-black font-black text-xs uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_20px_rgba(251,191,36,0.35)] flex items-center justify-center gap-2 cursor-pointer mt-3 active:scale-95"
                            >
                                <SparklesIcon className="w-4 h-4 fill-black" />
                                <span>{isRegistering ? "Démarrage en cours..." : (vipMediumName ? `Débloquer l'accès VIP à ${vipMediumName}` : (initialPack ? `Démarrer le Chat avec Sarah (${TRANSCASH_PACKS_CONFIG[selectedPack]?.price || '100'} €)` : "Démarrer le Chat avec Sarah"))}</span>
                            </button>
                        </form>

                        <div className="text-[10px] text-gray-400 text-center flex items-center justify-center gap-1.5 pt-1">
                            <ShieldIcon className="w-3.5 h-3.5 text-emerald-400" />
                            <span>Sécurisé & Confidentiel • Sans engagement</span>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                {/* Zone de Messages Fluide & 100% Opaque */}
                <div ref={messageContainerRef} onScroll={handleMessagesScroll} className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3.5 custom-scrollbar bg-[#080512]">
                    {messages.map((msg) => (
                        <div
                            key={msg.id}
                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                            <div
                                className={`max-w-[92%] sm:max-w-[85%] rounded-2xl px-3.5 py-3 sm:p-4 text-xs sm:text-sm leading-relaxed shadow-md ${
                                    msg.role === 'user'
                                        ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-tr-none'
                                        : 'bg-[#1a1233] border border-gold-500/30 text-gray-200 rounded-tl-none'
                                }`}
                            >
                                <div className="whitespace-pre-line font-sans">
                                    {renderMessageText(msg.text)}
                                </div>

                                {/* Si c'est le guide d'instructions, bouton d'accès direct en 1 clic */}
                                {msg.isStepGuide && !isCompleted && (
                                    <div className="mt-3 pt-3 border-t border-gold-500/20 space-y-2">
                                        <a
                                            href="https://www.recharge.fr/carte-transcash"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-full bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 hover:from-blue-500 hover:to-indigo-500 text-white font-bold py-2.5 px-3.5 rounded-xl text-xs flex items-center justify-center gap-2 shadow-lg transition-transform hover:scale-[1.02]"
                                        >
                                            <span>Recharger ({selectedPack}€)</span>
                                            <ExternalLinkIcon className="w-3.5 h-3.5 shrink-0" />
                                        </a>
                                        <div className="text-[10px] text-amber-200/90 text-center font-medium">
                                            Pensez à bien indiquer votre e-mail sur le site pour recevoir le code !
                                        </div>
                                    </div>
                                )}

                                {/* Reçu officiel stylisé si validation réussie */}
                                {msg.isReceipt && msg.receiptData && (
                                    <div className="mt-3 bg-black/70 border border-gold-500/50 rounded-xl p-3.5 text-white space-y-2.5">
                                        <div className="flex items-center justify-between border-b border-gold-500/30 pb-1.5">
                                            <div className="flex items-center gap-1 text-gold-400 font-bold text-xs uppercase tracking-wider">
                                                <SparklesIcon className="w-3.5 h-3.5" /> Reçu Officiel Validé
                                            </div>
                                            <span className="text-[10px] bg-green-500/20 text-green-300 font-bold px-2 py-0.5 rounded-full border border-green-500/30">
                                                Crédité
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                            <div>
                                                <span className="text-gray-400 text-[10px] block">Forfait :</span>
                                                <strong className="text-white truncate block">{msg.receiptData.packName}</strong>
                                            </div>
                                            <div>
                                                <span className="text-gray-400 text-[10px] block">Montant :</span>
                                                <strong className="text-gold-300">{msg.receiptData.price}€ (Transcash)</strong>
                                            </div>
                                            <div>
                                                <span className="text-gray-400 text-[10px] block">Minutes :</span>
                                                <strong className="text-green-400 font-bold">+{msg.receiptData.minutes} Min</strong>
                                            </div>
                                            <div>
                                                <span className="text-gray-400 text-[10px] block">Points :</span>
                                                <strong className="text-gold-400">+{msg.receiptData.points} Pts</strong>
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => {
                                                onClose();
                                                if (onConsultStart) onConsultStart();
                                            }}
                                            className="w-full mt-2 bg-gradient-to-r from-gold-500 to-yellow-400 hover:from-gold-400 hover:to-yellow-300 text-black font-extrabold py-2.5 px-3 rounded-xl text-xs uppercase tracking-wider text-center transition-all shadow-md"
                                        >
                                            Consulter un voyant dès maintenant
                                        </button>
                                    </div>
                                )}

                                <div className={`text-[9px] mt-1.5 ${msg.role === 'user' ? 'text-indigo-200 text-right' : 'text-gray-500'}`}>
                                    {formatSafeTime(msg.timestamp)}
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* État validation en cours */}
                    {isValidating && (
                        <div className="flex justify-start">
                            <div className="bg-mystic-900 border border-gold-500/50 rounded-2xl rounded-tl-none p-3 max-w-[85%] text-xs text-gold-300 flex items-center gap-2.5 animate-pulse shadow-lg">
                                <RefreshCwIcon className="w-4 h-4 text-gold-400 animate-spin shrink-0" />
                                <div>
                                    <div className="font-bold text-[11px]">Validation en cours...</div>
                                    <div className="text-[10px] text-gray-400">Sarah vérifie le code et crédite vos minutes.</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Indicateur de saisie Admin */}
                    {isTyping && (
                        <div className="flex items-center gap-2 text-xs text-gray-400 italic">
                            <div className="flex gap-1 items-center bg-white/5 px-2.5 py-1 rounded-full border border-white/5">
                                <span className="w-1.5 h-1.5 bg-gold-400 rounded-full animate-bounce"></span>
                                <span className="w-1.5 h-1.5 bg-gold-400 rounded-full animate-bounce [animation-delay:0.2s]"></span>
                                <span className="w-1.5 h-1.5 bg-gold-400 rounded-full animate-bounce [animation-delay:0.4s]"></span>
                                <span className="ml-1 text-[10px]">Sarah écrit...</span>
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                {/* Zone de Contrôle / Saisie Directe */}
                <div className="bg-[#150e29] border-t border-gold-500/40 p-2.5 sm:p-4 shrink-0 space-y-2">
                    {!isCompleted ? (
                        <form onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }} className="flex gap-2 items-center">
                            <input
                                type="text"
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                placeholder="Écrivez à Sarah ou collez votre code Transcash..."
                                className="flex-1 bg-[#080512] border border-gold-500/40 focus:border-gold-400 rounded-xl px-3.5 py-2.5 text-xs sm:text-sm text-white placeholder-gray-500 focus:outline-none transition-colors"
                            />
                            <button
                                type="submit"
                                disabled={!inputText.trim()}
                                className="bg-gradient-to-r from-gold-500 to-yellow-400 hover:from-gold-400 hover:to-yellow-300 text-black px-4 py-2.5 rounded-xl text-xs font-black transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center shrink-0 shadow-md cursor-pointer"
                                title="Envoyer"
                            >
                                <SendIcon className="w-4 h-4" />
                            </button>
                        </form>
                    ) : (
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 w-full">
                            <div className="flex items-center gap-1.5 text-xs text-green-400 font-bold">
                                <CheckCircleIcon className="w-4 h-4 text-green-400 shrink-0" />
                                <span>Forfait validé & crédité !</span>
                            </div>
                            <div className="flex gap-2 w-full sm:w-auto">
                                <button
                                    onClick={() => {
                                        setIsCompleted(false);
                                        setCouponCode('');
                                    }}
                                    className="flex-1 sm:flex-initial bg-gradient-to-r from-gold-500 to-yellow-500 hover:from-gold-400 hover:to-yellow-400 text-black text-xs font-black px-3.5 py-2 rounded-xl transition-all shadow-md shrink-0 whitespace-nowrap"
                                >
                                    Envoyer un autre code
                                </button>
                                <button
                                    onClick={onClose}
                                    className="flex-1 sm:flex-initial bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-3.5 py-2 rounded-xl transition-colors shrink-0"
                                >
                                    Fermer
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                </>
            )}

            {/* Modale Guide Boîte E-mail (Explications simples et bienveillantes) */}
                {showEmailGuideModal && (
                    <div className="absolute inset-0 bg-black/95 z-30 flex items-center justify-center p-4 animate-fade-in">
                        <div className="bg-[#140e2b] border border-gold-500 rounded-2xl p-4 sm:p-5 max-w-md w-full text-white space-y-3.5 shadow-2xl">
                            <div className="flex items-center justify-between border-b border-white/10 pb-2">
                                <h4 className="font-bold text-gold-300 text-sm flex items-center gap-2">
                                    Comment retrouver votre code par e-mail ?
                                </h4>
                                <button onClick={() => setShowEmailGuideModal(false)} className="text-gray-400 hover:text-white p-1">
                                    <XIcon className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="space-y-2.5 text-xs text-gray-300 leading-relaxed">
                                <p className="font-medium text-white">
                                    Lorsque vous réglez sur le site Recharge.fr, le code vous est envoyé instantanément dans votre courrier électronique :
                                </p>
                                <div className="bg-[#0b0816] p-3 rounded-xl border border-white/10 space-y-1.5">
                                    <div>1. <strong>Ouvrez votre boîte e-mail habituelle</strong> (Orange, Gmail, SFR, Yahoo, Outlook...).</div>
                                    <div>2️⃣ Recherchez l'e-mail provenant de <strong>Recharge.fr</strong>.</div>
                                    <div>3. <em>Astuce :</em> Si vous ne le voyez pas, regardez dans vos <strong>"Spams" ou "Courriers indésirables"</strong>.</div>
                                    <div>4️⃣ Votre <strong>code à 12 chiffres</strong> est écrit à l'intérieur. Notez-le et venez le taper dans la case de notre discussion.</div>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowEmailGuideModal(false)}
                                className="w-full bg-gold-500 hover:bg-gold-400 text-black font-bold py-2.5 rounded-xl text-xs uppercase"
                            >
                                J'ai compris, je consulte mes e-mails
                            </button>
                        </div>
                    </div>
                )}

                {/* Modale Guide Bureau de Tabac */}
                {showTabacModal && (
                    <div className="absolute inset-0 bg-black/95 z-30 flex items-center justify-center p-4 animate-fade-in">
                        <div className="bg-[#140e2b] border border-gold-500 rounded-2xl p-4 sm:p-5 max-w-sm w-full text-white space-y-3.5 shadow-2xl">
                            <div className="flex items-center justify-between border-b border-white/10 pb-2">
                                <h4 className="font-bold text-gold-300 text-sm flex items-center gap-2">
                                    En Bureau de Tabac / Presse
                                </h4>
                                <button onClick={() => setShowTabacModal(false)} className="text-gray-400 hover:text-white p-1">
                                    <XIcon className="w-4 h-4" />
                                </button>
                            </div>
                            <p className="text-gray-300 text-xs leading-relaxed">
                                Les coupons Transcash sont disponibles chez <strong>plus de 35 000 buralistes</strong> en France, Belgique et Suisse.
                            </p>
                            <div className="bg-[#0b0816] p-3 rounded-xl border border-gold-500/30 text-xs text-gray-200">
                                Demandez simplement à votre buraliste : <br/>
                                <strong className="text-gold-300 text-sm block mt-1">"Un coupon recharge Transcash de {selectedPack}€ s'il vous plaît."</strong>
                            </div>
                            <p className="text-[11px] text-gray-400">
                                Le buraliste vous remet un ticket de caisse contenant votre code secret à 12 chiffres.
                            </p>
                            <button
                                onClick={() => setShowTabacModal(false)}
                                className="w-full bg-gold-500 hover:bg-gold-400 text-black font-bold py-2.5 rounded-xl text-xs uppercase"
                            >
                                Compris, j'ai mon code sur ticket
                            </button>
                        </div>
                    </div>
                )}

            </div>
    );
};

export default AdminPurchaseChat;
